import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { getMyAccess } from "app/api/creation/ApiService";

// The signed-in admin's role and permissions, loaded from GET admin/access.
// This only shapes the UI (menu, routes, buttons) - the backend enforces every
// permission on every request, so nothing here is a security boundary.
//
// Module keys ("brand", "blogPost", ...) and actions ("create" | "read" |
// "update" | "delete") match the backend's permit() guards. Permissions are
// never kept in a cookie or storage; they're re-fetched on sign-in, when the
// tab regains focus, and after any 403 (the role may have just changed).

const PermissionsContext = createContext(undefined);

const TOKEN_COOKIE = "accruewise-token";
const FOCUS_REFRESH_MS = 60_000;
const FORBIDDEN_REFRESH_MS = 5_000;
export const FORBIDDEN_EVENT = "accruewise:forbidden";

const EMPTY_ACCESS = { role: null, isRoot: false, permissions: {} };

export function PermissionsProvider({ children }) {
    // Re-read on every navigation so signing in/out is picked up.
    useLocation();
    const token = Cookies.get(TOKEN_COOKIE);
    const [state, setState] = useState({ ...EMPTY_ACCESS, loading: !!token, error: false });
    const lastFetchAt = useRef(0);
    // Only the latest request may update state: a slow response from before a
    // sign-in/sign-out must not overwrite the current session's access.
    const requestSeq = useRef(0);

    const refresh = useCallback(async () => {
        const seq = ++requestSeq.current;
        const tokenAtStart = Cookies.get(TOKEN_COOKIE);
        if (!tokenAtStart) {
            setState({ ...EMPTY_ACCESS, loading: false, error: false });
            return;
        }
        const isStale = () => seq !== requestSeq.current || Cookies.get(TOKEN_COOKIE) !== tokenAtStart;
        lastFetchAt.current = Date.now();
        try {
            const res = await getMyAccess();
            if (isStale()) return;
            const data = res?.data?.data;
            if (!res?.data?.status || !data) throw new Error(res?.data?.message);
            setState({
                role: data.role || null,
                isRoot: !!data.isRoot,
                permissions: data.permissions || {},
                loading: false,
                error: false,
            });
        } catch {
            if (isStale()) return;
            // Keep whatever we had; deny-by-default if nothing loaded yet.
            setState((s) => ({ ...s, loading: false, error: true }));
        }
    }, []);

    useEffect(() => {
        if (!token) {
            requestSeq.current++; // drop any in-flight response from the old session
            setState({ ...EMPTY_ACCESS, loading: false, error: false });
            return;
        }
        setState((s) => ({ ...s, loading: true }));
        refresh();
    }, [token, refresh]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible" && Date.now() - lastFetchAt.current > FOCUS_REFRESH_MS) {
                refresh();
            }
        };
        const onForbidden = () => {
            if (Date.now() - lastFetchAt.current > FORBIDDEN_REFRESH_MS) refresh();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener(FORBIDDEN_EVENT, onForbidden);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener(FORBIDDEN_EVENT, onForbidden);
        };
    }, [refresh]);

    const can = useCallback(
        (module, action = "read") => state.isRoot || !!state.permissions?.[module]?.includes(action),
        [state.isRoot, state.permissions]
    );

    const value = useMemo(() => ({ ...state, can, refresh }), [state, can, refresh]);
    return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error("usePermissions must be used within a PermissionsProvider");
    }
    return context;
}

// const { canCreate, canRead, canUpdate, canDelete } = usePermission("brand");
export function usePermission(module) {
    const { can } = usePermissions();
    return useMemo(
        () => ({
            canCreate: can(module, "create"),
            canRead: can(module, "read"),
            canUpdate: can(module, "update"),
            canDelete: can(module, "delete"),
        }),
        [can, module]
    );
}

// <Can module="brand" action="delete">...</Can>
export function Can({ module, action = "read", fallback = null, children }) {
    const { can } = usePermissions();
    return can(module, action) ? children : fallback;
}

// Route/menu permission spec: "brand" (= read), { module, action }, or an
// array of those meaning "any of". No spec = open to every signed-in admin.
export const normalizePermissionSpec = (spec) =>
    typeof spec === "string" ? { module: spec, action: "read" } : spec;

export const isAllowed = (can, spec) => {
    if (!spec) return true;
    if (Array.isArray(spec)) return spec.some((s) => isAllowed(can, s));
    const { module, action } = normalizePermissionSpec(spec);
    return can(module, action);
};
